// Reads back the website's lead notification emails, pasted as text, into
// leads the server can import with their real dates (POST /admin/leads/import).
//
// The format is the one plexii-site/brochure/api/leads.js writes:
//
//   Demo request from the Plexii website.
//
//   Name: Sam Rivera
//   Email: sam@example.com
//   Company: Rivera & Co
//   Message: …
//   Page: https://plexiidesk.com/book?utm_source=…
//   Received: 2026-10-15T23:14:00.000Z
//
//   Reply to this email to answer Sam Rivera directly.
//
// Several emails can be pasted at once; each starts at its "… from the Plexii
// website." line. A line that is not a known label continues the field above
// it (a message with line breaks). Nothing is guessed: a lead without a
// Received line is returned with a problem rather than dated today.

import type { LeadImportItem } from './adminClient'

export interface ParsedLead {
  item: Partial<LeadImportItem>
  problems: string[]
}

const KIND_BY_LABEL: Record<string, LeadImportItem['kind']> = {
  'live session registration': 'live',
  'demo request': 'demo',
  'early-access signup': 'early',
  'download link request': 'download'
}

const FIELD_BY_LABEL: Record<string, keyof LeadImportItem> = {
  name: 'name',
  email: 'email',
  company: 'company',
  role: 'role',
  'team size': 'teamSize',
  message: 'message',
  page: 'page',
  received: 'at'
}

const HEADER = /^\s*(live session registration|demo request|early-access signup|download link request) from the plexii website\.?\s*$/i
const FIELD = /^\s*(name|email|company|role|team size|message|page|received)\s*:\s?(.*)$/i
const FOOTER = /^\s*reply to this email to answer\b/i
const EMAIL = /^[^@\s,;<>"]+@[^@\s,;<>"]+\.[^@\s,;<>"]+$/

export function parseLeadEmails(text: string): ParsedLead[] {
  const out: ParsedLead[] = []
  let cur: ParsedLead | null = null
  let last: keyof LeadImportItem | null = null
  for (const raw of text.replace(/\r\n?/g, '\n').split('\n')) {
    const header = HEADER.exec(raw)
    if (header) {
      cur = { item: { kind: KIND_BY_LABEL[header[1].toLowerCase()] }, problems: [] }
      out.push(cur)
      last = null
      continue
    }
    if (!cur) continue
    if (FOOTER.test(raw)) { last = null; continue }
    const field = FIELD.exec(raw)
    if (field) {
      last = FIELD_BY_LABEL[field[1].toLowerCase()]
      ;(cur.item as Record<string, string>)[last] = field[2].trim()
      continue
    }
    // A wrapped message line; blank lines between fields end nothing.
    if (last === 'message' && raw.trim()) {
      cur.item.message = `${cur.item.message ?? ''}\n${raw.trim()}`.trim()
    }
  }
  for (const p of out) {
    if (!p.item.email || !EMAIL.test(p.item.email)) p.problems.push('no usable email address')
    if (!p.item.at) p.problems.push('no Received date in the email')
    else if (!Number.isFinite(Date.parse(p.item.at))) p.problems.push(`the Received date "${p.item.at}" cannot be read`)
  }
  return out
}
